/** Client Task Management (/api/v1/tasks) — contrat du module Go nesit-tasks
 * (internal/api/rest + internal/domain, json tags recopiés). */
import { tasksRequest } from "./client";
import type { Priority, Status, RaciRole, AclLevel } from "../lib/design";

export interface Paged<T> {
  items: T[];
  next_cursor?: string;
}

export type TaskVisibility = "private" | "team" | "tenant";
export type ActorType = "user" | "team" | "contact" | "system";

/** Agrégat Task (domain/task.go) — soft references uniquement (context_id,
 * reporter_actor_ref) ; le RACI vit dans /assignments. */
export interface TmTask {
  id: string;
  tenant_id: string;
  list_id?: string;
  parent_task_id?: string;
  title: string;
  description?: string;
  status: Status | "archived";
  priority: Priority;
  created_by_user_ref: string;
  updated_by_user_ref?: string;
  reporter_actor_type?: ActorType;
  reporter_actor_ref?: string;
  visibility: TaskVisibility;
  team_refs: string[];
  due_date?: string;
  reminder_at?: string;
  completed_at?: string;
  tags: string[];
  position: number;
  context_type?: string;
  context_id?: string;
  context_label?: string;
  recurrence_rule?: string;
  archived_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TmAssignment {
  id: string;
  tenant_id: string;
  task_id: string;
  actor_type: ActorType;
  actor_ref: string;
  role: RaciRole;
  active: boolean;
  created_at: string;
}

export interface TmComment {
  id: string;
  tenant_id: string;
  task_id: string;
  author_user_ref: string;
  author_actor_type?: ActorType;
  author_actor_ref?: string;
  body: string;
  mentions: string[];
  created_at: string;
  updated_at: string;
}

export interface TmAttachment {
  id: string;
  tenant_id: string;
  task_id: string;
  type: string;
  filename?: string;
  size_bytes?: number;
  storage_uri?: string;
  uploaded_by: string;
  created_at: string;
}

export interface TmBlocker {
  id: string;
  tenant_id: string;
  blocked_task_id: string;
  blocker_task_id: string;
  type: string;
  created_at: string;
}

export interface TmAclEntry {
  id: string;
  tenant_id: string;
  task_id: string;
  subject_type: ActorType;
  subject_ref: string;
  access_level: AclLevel;
  created_at: string;
}

export interface TmListEntity {
  id: string;
  tenant_id: string;
  owner_id?: string;
  name: string;
  description?: string;
  visibility: TaskVisibility;
  team_ids: string[];
  icon?: string;
  color?: string;
  position: number;
  archived_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TmTemplateNode {
  title: string;
  description?: string;
  priority?: string;
  children?: TmTemplateNode[];
}

export interface TmTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  structure: { nodes: TmTemplateNode[] };
  is_system: boolean;
  created_at: string;
}

/** Item du feed unifié GET /me — source "native" = tâche TM, sinon miroir
 * répliqué lecture seule (work items PM, DT F-04). */
export interface MeFeedItem {
  source: string;
  id: string;
  tenant_id: string;
  title: string;
  status: string;
  priority?: string;
  due_date?: string;
  context_label?: string;
  source_url?: string;
  sort_at: string;
}

export interface TmDashboard {
  total: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  overdue: number;
}

export interface CreateTaskInput {
  title: string;
  list_id?: string;
  parent_task_id?: string;
  description?: string;
  priority?: Priority;
  reporter_actor_type?: ActorType;
  reporter_actor_ref?: string;
  visibility?: TaskVisibility;
  due_date?: string;
  reminder_at?: string;
  tags?: string[];
  context_type?: string;
  context_id?: string;
  recurrence_rule?: string;
  template_id?: string;
  assignments?: { actor_type: ActorType; actor_ref: string; role: RaciRole }[];
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  status?: Status;
  priority?: Priority;
  visibility?: TaskVisibility;
  due_date?: string | null;
  reminder_at?: string | null;
  tags?: string[];
}

// ── Tasks ────────────────────────────────────────────────────────────────────

export function listTasks(params?: {
  status?: string;
  list_id?: string;
  cursor?: string;
}): Promise<Paged<TmTask>> {
  return tasksRequest({ method: "GET", url: "", params });
}

export function getTask(id: string): Promise<TmTask> {
  return tasksRequest({ method: "GET", url: `/${id}` });
}

export function createTask(body: CreateTaskInput): Promise<TmTask> {
  return tasksRequest({ method: "POST", url: "", data: body });
}

export function updateTask(id: string, body: UpdateTaskInput): Promise<TmTask> {
  return tasksRequest({ method: "PATCH", url: `/${id}`, data: body });
}

export function deleteTask(id: string): Promise<void> {
  return tasksRequest({ method: "DELETE", url: `/${id}` });
}

export function completeTask(id: string): Promise<TmTask> {
  return tasksRequest({ method: "POST", url: `/${id}/complete`, data: {} });
}

export function listSubtasks(id: string): Promise<Paged<TmTask>> {
  return tasksRequest({ method: "GET", url: `/${id}/subtasks` });
}

export function getMyFeed(cursor?: string): Promise<Paged<MeFeedItem>> {
  return tasksRequest({
    method: "GET",
    url: "/me",
    params: { limit: 200, ...(cursor ? { cursor } : {}) },
  });
}

export function getDashboard(): Promise<TmDashboard> {
  return tasksRequest({ method: "GET", url: "/dashboard" });
}

// ── Assignments (RACI) ───────────────────────────────────────────────────────

export function listAssignments(taskId: string): Promise<Paged<TmAssignment>> {
  return tasksRequest({ method: "GET", url: `/${taskId}/assignments` });
}

export function addAssignment(
  taskId: string,
  body: { actor_type: ActorType; actor_ref: string; role: RaciRole },
): Promise<TmAssignment> {
  return tasksRequest({
    method: "POST",
    url: `/${taskId}/assignments`,
    data: body,
  });
}

export function removeAssignment(
  taskId: string,
  assignmentId: string,
): Promise<void> {
  return tasksRequest({
    method: "DELETE",
    url: `/${taskId}/assignments/${assignmentId}`,
  });
}

// ── Comments ─────────────────────────────────────────────────────────────────

export function listComments(taskId: string): Promise<Paged<TmComment>> {
  return tasksRequest({ method: "GET", url: `/${taskId}/comments` });
}

export function addComment(
  taskId: string,
  body: { body: string; mentions?: string[] },
): Promise<TmComment> {
  return tasksRequest({
    method: "POST",
    url: `/${taskId}/comments`,
    data: body,
  });
}

// ── Attachments ──────────────────────────────────────────────────────────────

export function listAttachments(taskId: string): Promise<Paged<TmAttachment>> {
  return tasksRequest({ method: "GET", url: `/${taskId}/attachments` });
}

export function addAttachment(
  taskId: string,
  body: {
    type: string;
    filename?: string;
    size_bytes?: number;
    storage_uri: string;
  },
): Promise<TmAttachment> {
  return tasksRequest({
    method: "POST",
    url: `/${taskId}/attachments`,
    data: body,
  });
}

// ── Blockers (dépendances locales) ───────────────────────────────────────────

export function listBlockers(taskId: string): Promise<Paged<TmBlocker>> {
  return tasksRequest({ method: "GET", url: `/${taskId}/blockers` });
}

export function addBlocker(
  taskId: string,
  blockerTaskId: string,
): Promise<TmBlocker> {
  return tasksRequest({
    method: "POST",
    url: `/${taskId}/blockers`,
    data: { blocker_task_id: blockerTaskId },
  });
}

export function removeBlocker(
  taskId: string,
  blockerId: string,
): Promise<void> {
  return tasksRequest({
    method: "DELETE",
    url: `/${taskId}/blockers/${blockerId}`,
  });
}

// ── ACL (partage) ────────────────────────────────────────────────────────────

export function listAcl(taskId: string): Promise<Paged<TmAclEntry>> {
  return tasksRequest({ method: "GET", url: `/${taskId}/acl` });
}

export function addAcl(
  taskId: string,
  body: {
    subject_type: ActorType;
    subject_ref: string;
    access_level: AclLevel;
  },
): Promise<TmAclEntry> {
  return tasksRequest({ method: "POST", url: `/${taskId}/acl`, data: body });
}

export function removeAcl(taskId: string, aclId: string): Promise<void> {
  return tasksRequest({ method: "DELETE", url: `/${taskId}/acl/${aclId}` });
}

// ── Lists ────────────────────────────────────────────────────────────────────

export function listLists(): Promise<Paged<TmListEntity>> {
  return tasksRequest({ method: "GET", url: "/lists" });
}

export function createList(body: {
  name: string;
  description?: string;
  visibility?: TaskVisibility;
  team_ids?: string[];
  icon?: string;
  color?: string;
}): Promise<TmListEntity> {
  return tasksRequest({ method: "POST", url: "/lists", data: body });
}

export function updateList(
  id: string,
  body: Partial<{
    name: string;
    description: string;
    visibility: TaskVisibility;
    team_ids: string[];
    icon: string;
    color: string;
    position: number;
  }>,
): Promise<TmListEntity> {
  return tasksRequest({ method: "PATCH", url: `/lists/${id}`, data: body });
}

export function deleteList(id: string): Promise<void> {
  return tasksRequest({ method: "DELETE", url: `/lists/${id}` });
}

// ── Templates ────────────────────────────────────────────────────────────────

export function listTemplates(): Promise<Paged<TmTemplate>> {
  return tasksRequest({ method: "GET", url: "/templates" });
}
